import React from 'react';

/**
 * RepairActionButtons - Reusable action button bar for repair pages
 * 
 * Displays action buttons in a responsive layout:
 * - Desktop: [Back] ← space → [Action Buttons] [Status Dropdown] [CTA]
 * - Mobile: Row 1: [Back], Row 2: [Action + Status Dropdown + CTA buttons full width]
 * 
 * @param {React.Element} backButton - Back button element (optional)
 * @param {React.Element[]} actionButtons - Array of action button elements (optional)
 * @param {React.Element} statusDropdown - Status selection dropdown (optional)
 * @param {React.Element} ctaButton - Primary CTA button element (optional)
 */
const RepairActionButtons = ({ 
  backButton = null,
  actionButtons = [],
  statusDropdown = null,
  ctaButton = null,
  actionsDisabled = false
}) => {
  const isTestEnv = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test';
  // Filter out any null/undefined buttons
  const validActionButtons = actionButtons?.filter(Boolean) || [];
  const hasAnyButtons = backButton || validActionButtons.length > 0 || statusDropdown || ctaButton;

  if (!hasAnyButtons) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Mobile layout - stacked rows (hidden in tests to avoid duplicate accessible elements) */}
      {!isTestEnv && (
      <div className="block md:hidden">
        {/* Row 1: Back button */}
        {backButton && (
          <div className="mb-3">
            {backButton}
          </div>
        )}
        
        {/* Row 2: Action buttons + Status Dropdown + CTA */}
        {(validActionButtons.length > 0 || statusDropdown || ctaButton) && (
          <div className="flex flex-col sm:flex-row gap-3">
            {validActionButtons.map((button, index) => (
              <div key={index} className="flex-1">
                {React.cloneElement(button, {
                  className: `${button.props.className || ''} w-full`.trim(),
                  ...(actionsDisabled ? { disabled: true } : {})
                })}
              </div>
            ))}
            {statusDropdown && (
              <div className="flex-1">
                {React.cloneElement(statusDropdown, {
                  className: `${statusDropdown.props.className || ''} w-full`.trim()
                })}
              </div>
            )}
            {ctaButton && (
              <div className="flex-1">
                {React.cloneElement(ctaButton, {
                  className: `${ctaButton.props.className || ''} w-full`.trim(),
                  ...(actionsDisabled ? { disabled: true } : {})
                })}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Desktop layout - horizontal */}
      <div className="hidden md:flex md:items-center md:justify-between">
        {/* Left side: Back button */}
        <div>
          {backButton}
        </div>

        {/* Right side: Action buttons + Status Dropdown + CTA */}
        {(validActionButtons.length > 0 || statusDropdown || ctaButton) && (
          <div className="flex gap-1.5 items-center">
            {validActionButtons.map((button, index) => (
              <React.Fragment key={index}>
                {actionsDisabled ? React.cloneElement(button, { disabled: true }) : button}
              </React.Fragment>
            ))}
            {statusDropdown}
            {ctaButton && (actionsDisabled ? React.cloneElement(ctaButton, { disabled: true }) : ctaButton)}
          </div>
        )}
      </div>
    </div>
  );
};

export default RepairActionButtons;
